import React from 'react';

const SkeletonBlock: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse ${className}`} />
);

const DashboardSkeleton: React.FC = () => {
  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-[1400px] mx-auto space-y-5 p-5">
        {/* Greeting bar skeleton */}
        <div className="rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 p-6">
          <div className="flex items-center justify-between">
            <div>
              <SkeletonBlock className="h-7 w-64 mb-2" />
              <SkeletonBlock className="h-4 w-44" />
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <SkeletonBlock key={i} className="h-9 w-24 rounded-lg" />
              ))}
            </div>
          </div>
        </div>

        {/* Metrics grid skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 p-4"
            >
              <SkeletonBlock className="h-9 w-9 rounded-lg mb-3" />
              <SkeletonBlock className="h-7 w-14 mb-1" />
              <SkeletonBlock className="h-3.5 w-20" />
            </div>
          ))}
        </div>

        {/* Urgent panels skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2].map((panel) => (
            <div
              key={panel}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden"
            >
              <div className="px-5 py-3.5 bg-gray-50/50 dark:bg-gray-750/50 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <SkeletonBlock className="h-7 w-7 rounded-lg" />
                <SkeletonBlock className="h-4 w-44" />
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700/70">
                {[1, 2, 3, 4].map((row) => (
                  <div key={row} className="px-5 py-3 flex items-center gap-3">
                    <SkeletonBlock className="h-7 w-7 rounded-full" />
                    <div className="flex-1">
                      <SkeletonBlock className="h-4 w-3/4 mb-1.5" />
                      <SkeletonBlock className="h-3 w-1/2" />
                    </div>
                    <SkeletonBlock className="h-5 w-14 rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Charts skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2].map((chart) => (
            <div key={chart} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden">
              <div className="px-5 py-3.5 bg-gray-50/50 dark:bg-gray-750/50 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <SkeletonBlock className="h-7 w-7 rounded-lg" />
                <SkeletonBlock className="h-4 w-40" />
              </div>
              <div className="p-5">
                <SkeletonBlock className="h-48 rounded-lg" />
              </div>
            </div>
          ))}
        </div>

        {/* Bottom row skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2].map((section) => (
            <div key={section} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden">
              <div className="px-5 py-3.5 bg-gray-50/50 dark:bg-gray-750/50 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
                <SkeletonBlock className="h-7 w-7 rounded-lg" />
                <SkeletonBlock className="h-4 w-36" />
              </div>
              {[1, 2, 3, 4].map((row) => (
                <div key={row} className="px-5 py-3 flex items-center gap-3">
                  <SkeletonBlock className="h-2 w-2 rounded-full" />
                  <SkeletonBlock className="h-4 flex-1" />
                  <SkeletonBlock className="h-3 w-12" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardSkeleton;
